import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Header link to /messages for authors who have an AE attribution.
 *
 * Renders nothing when:
 *   - User isn't logged in (parent gates this)
 *   - User has no AE (silent — keeps the header tidy for regular readers)
 *
 * Polls `/api/me/ae-thread/unread-count` every 60s. Always cheap (single
 * indexed find_one) so this won't add meaningful load.
 */
export const AEMessagesLink = ({ user }) => {
  const [unread, setUnread] = useState(0);
  const [hasAe, setHasAe] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    // Skip for AEs and admins — they have their own portals.
    if (user.role === 'AE' || user.role === 'ADMIN') return undefined;

    let alive = true;
    const tick = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/me/ae-thread/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        setUnread(res.data?.unread || 0);
        setHasAe(!!res.data?.has_ae);
      } catch {
        /* silent — header chrome shouldn't surface errors */
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  if (!user || !hasAe) return null;

  return (
    <Link
      to="/messages"
      data-testid="nav-messages"
      className="relative text-dark-text-secondary hover:text-dark-text flex items-center gap-1 text-sm lg:text-base"
      title="Messages"
    >
      <MessageSquare size={18} />
      <span className="hidden lg:inline">Messages</span>
      {unread > 0 && (
        <span
          data-testid="nav-messages-badge"
          className="absolute -top-1 -right-1 lg:static lg:ml-1 bg-purple-500 text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center font-semibold"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
};
